// Debug Console Implementation

import { copyToClipboard, showToast } from './utils';

export class DebugConsole {
  private logs: string[] = [];
  private startTime: number = Date.now();
  private maxLogs: number = 100;
  private panel: HTMLElement | null = null;
  private logsContainer: HTMLElement | null = null;

  constructor() {
    this.panel = document.getElementById('debugPanel');
    this.logsContainer = document.getElementById('debugLogs');
    this.setupEventListeners();
  }

  /**
   * Log a message with timestamp and elapsed time
   */
  log(message: string): void {
    const now = Date.now();
    const timestamp = new Date(now).toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3,
    } as any);
    const elapsed = ((now - this.startTime) / 1000).toFixed(3);

    const logEntry = `[${timestamp}] [+${elapsed}s] ${message}`;
    this.logs.push(logEntry);

    // Keep only last maxLogs
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    this.render();

    // Also log to console
    console.log(`[Librario Debug] ${message}`);
  }

  /**
   * Log an error message (displayed in red)
   */
  error(message: string): void {
    const now = Date.now();
    const timestamp = new Date(now).toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3,
    } as any);
    const elapsed = ((now - this.startTime) / 1000).toFixed(3);

    const logEntry = `<span style="color: #ff4444;">[${timestamp}] [+${elapsed}s] ❌ ${message}</span>`;
    this.logs.push(logEntry);

    // Keep only last maxLogs
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    this.render();

    // Also log to console
    console.error(`[Librario Debug] ${message}`);
  }

  /**
   * Clear all logs
   */
  clear(): void {
    this.logs = [];
    this.startTime = Date.now();
    this.render();
    this.log('Debug console cleared');
  }

  /**
   * Copy logs to clipboard
   */
  async copy(): Promise<void> {
    const text = this.logs.join('\n');
    const success = await copyToClipboard(text);

    if (success) {
      showToast('📋 Logs copied to clipboard', 'success');
      this.log('Logs copied to clipboard');
    } else {
      showToast('Failed to copy logs', 'error');
      this.log('Failed to copy logs to clipboard');
    }
  }

  /**
   * Show debug panel
   */
  show(): void {
    if (this.panel) {
      this.panel.style.display = 'block';
      this.log('Debug console activated');

      // Haptic feedback on mobile
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
    }
  }

  /**
   * Hide debug panel
   */
  hide(): void {
    if (this.panel) {
      this.panel.style.display = 'none';
    }
  }

  /**
   * Toggle debug panel visibility
   */
  toggle(): void {
    if (this.panel) {
      if (this.panel.style.display === 'none' || this.panel.style.display === '') {
        this.show();
      } else {
        this.hide();
      }
    }
  }

  /**
   * Render logs to DOM
   */
  private render(): void {
    if (!this.logsContainer) return;

    this.logsContainer.innerHTML = this.logs
      .map((log) => `<div class="log-entry">${this.escapeHtml(log)}</div>`)
      .join('');

    // Auto-scroll to bottom
    this.logsContainer.scrollTop = this.logsContainer.scrollHeight;
  }

  /**
   * Setup event listeners for debug console
   */
  private setupEventListeners(): void {
    // Copy button
    const copyBtn = document.getElementById('copyDebugLogs');
    if (copyBtn) {
      copyBtn.addEventListener('click', () => this.copy());
    }

    // Clear button
    const clearBtn = document.getElementById('clearDebugLogs');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => this.clear());
    }

    // Close button
    const closeBtn = document.getElementById('closeDebug');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.hide());
    }

    // Long-press activation on title
    this.setupLongPressActivation();
  }

  /**
   * Setup long-press activation (800ms) on main title
   */
  private setupLongPressActivation(): void {
    const title = document.getElementById('main-title');
    if (!title) return;

    let longPressTimer: ReturnType<typeof setTimeout> | null = null;

    const startPress = () => {
      longPressTimer = setTimeout(() => {
        this.show();
      }, 800);
    };

    const endPress = () => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        longPressTimer = null;
      }
    };

    // Mouse events
    title.addEventListener('mousedown', startPress);
    title.addEventListener('mouseup', endPress);
    title.addEventListener('mouseleave', endPress);

    // Touch events
    title.addEventListener('touchstart', (e) => {
      e.preventDefault();
      startPress();
    });
    title.addEventListener('touchend', endPress);
    title.addEventListener('touchcancel', endPress);
  }

  /**
   * Escape HTML to prevent XSS
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Create global debug console instance
export const debugConsole = new DebugConsole();
